
import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { Card } from './Card';
import { Spine } from './Spine';
import { PROJECT_COUNT, ITEM_SPACING, PROJECTS } from '../constants';
import { Section } from '../App';

// Fix R3F intrinsic element type errors by using type-safe component wrappers
const ThreeColor = 'color' as any;
const ThreeFog = 'fog' as any;
const ThreeAmbientLight = 'ambientLight' as any;
const ThreeSpotLight = 'spotLight' as any;
const ThreePointLight = 'pointLight' as any;
const ThreeGroup = 'group' as any;
const ThreeMesh = 'mesh' as any;
const ThreeIcosahedronGeometry = 'icosahedronGeometry' as any;
const ThreeMeshStandardMaterial = 'meshStandardMaterial' as any;

interface SceneProps {
  onProjectChange: (index: number) => void;
  progress: number;
  activeSection: Section;
}

export const Scene: React.FC<SceneProps> = ({ onProjectChange, progress, activeSection }) => {
  const { camera } = useThree();
  const velocityRef = useRef(0);
  const lastProgress = useRef(0);
  const smoothedProgress = useRef(progress);
  const scrollRefProxy = useRef(0);

  // Expanded waypoints to lock the camera for almost the entire Works range (0.12 to 0.85)
  const waypoints = [
    [0, 0, 15, 0, 0, 0],         // 0: HERO
    [0, 0.2, 8.5, 0, 0, 0],      // 1: WORKS ENTRY (0.16)
    [0, 0.0, 8.5, 0.0, 0, 0],    // 2: WORKS DWELL 1 (0.32)
    [0, 0.0, 8.5, 0.0, 0, 0],    // 3: WORKS DWELL 2 (0.48)
    [0, 0.0, 8.5, 0.0, 0, 0],    // 4: WORKS DWELL 3 (0.64)
    [0, 0.0, 8.5, 0.0, 0, 0],    // 5: WORKS FINAL FOCUS (0.80)
    [0, 0, 25, 0.1, 0, 0],       // 6: SOLUTIONS (0.96)
    [0, -15, 20, 0.6, 0, 0]      // 7: CONTACT (1.0)
  ];

  useEffect(() => {
    gsap.from(camera.position, {
      z: 30,
      duration: 3,
      ease: "power4.out"
    });
  }, [camera]);

  useFrame((state, delta) => {
    // SMOOTHING ENGINE
    smoothedProgress.current = THREE.MathUtils.lerp(
      smoothedProgress.current, 
      progress, 
      0.14
    );

    const p = smoothedProgress.current;

    velocityRef.current = (p - lastProgress.current) / delta;
    lastProgress.current = p;

    // CAMERA MOVEMENT
    const segmentCount = waypoints.length - 1;
    const scaledProgress = p * segmentCount;
    const index = Math.floor(scaledProgress);
    const weight = scaledProgress % 1;

    if (index < segmentCount) {
      const p1 = waypoints[index];
      const p2 = waypoints[index + 1];

      camera.position.x = THREE.MathUtils.lerp(p1[0], p2[0], weight);
      camera.position.y = THREE.MathUtils.lerp(p1[1], p2[1], weight);
      camera.position.z = THREE.MathUtils.lerp(p1[2], p2[2], weight);
      
      camera.rotation.x = THREE.MathUtils.lerp(p1[3], p2[3], weight);
      camera.rotation.y = THREE.MathUtils.lerp(p1[4], p2[4], weight);
      camera.rotation.z = THREE.MathUtils.lerp(p1[5], p2[5], weight);
    }

    // GALLERY SYNC LOGIC - Syncs with WORKS section
    // Starts earlier (0.05) to ensure cards are ready
    if (p > 0.05 && p < 0.88) {
      // Adjusted smoothstep start from 0.12 to 0.08 so animation begins sooner
      const galleryProgress = THREE.MathUtils.smoothstep(p, 0.08, 0.82);
      const activeIndex = Math.min(PROJECT_COUNT - 1, Math.floor(galleryProgress * PROJECT_COUNT));
      onProjectChange(activeIndex);
    }

    // CARD SCROLL SYNC
    // Adjusted smoothstep start from 0.12 to 0.08
    const galleryScrollProgress = THREE.MathUtils.smoothstep(p, 0.08, 0.82);
    const maxScrollDist = (PROJECT_COUNT - 1) * ITEM_SPACING;
    scrollRefProxy.current = galleryScrollProgress * maxScrollDist;
  });

  return (
    <>
      <ThreeColor attach="background" args={['#050505']} />
      <ThreeFog attach="fog" args={['#050505', 8, 40]} />
      
      <ThreeAmbientLight intensity={0.5} />
      <ThreeSpotLight position={[10, 20, 10]} angle={0.3} penumbra={1} intensity={150} />
      <ThreePointLight position={[-10, 5, 5]} intensity={50} color="#4444ff" />

      <ThreeGroup>
        <Spine velocityRef={velocityRef} />
        
        {/* Visibility starts significantly earlier (0.05 vs 0.12) */}
        <ThreeGroup visible={smoothedProgress.current > 0.05 && smoothedProgress.current < 0.90}>
          {PROJECTS.map((project, i) => (
            <Card 
              key={i} 
              index={i} 
              project={project}
              scrollRef={scrollRefProxy} 
              velocityRef={velocityRef}
              // Card fade-in trigger starts earlier (0.05 vs 0.14)
              isVisible={smoothedProgress.current > 0.05 && smoothedProgress.current < 0.86}
            />
          ))}
        </ThreeGroup>

        <ThreeMesh position={[0, 6, -8]} rotation-y={smoothedProgress.current * 3}>
          <ThreeIcosahedronGeometry args={[5, 1]} />
          <ThreeMeshStandardMaterial 
            color="#ffffff" 
            wireframe 
            transparent 
            opacity={Math.max(0, 0.02 - Math.abs(smoothedProgress.current - 0.05) * 0.2)} 
          />
        </ThreeMesh>
      </ThreeGroup>
    </>
  );
};
